<template>
  <div class="wallpaper-grid">
    <!-- Image Viewer Modal -->
    <ImageViewer v-if="selectedWallpaper" :src="selectedWallpaper.imageUrl"
      :wallpaper-id="selectedWallpaper.id"
      :alt="selectedWallpaper.title || 'Wallpaper'" @close="selectedWallpaper = null" />

    <!-- Loading skeleton -->
    <div v-if="loading && wallpapers.length === 0" class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
      <div v-for="n in 10" :key="`skeleton-${n}`"
        class="rounded-xl overflow-hidden animate-pulse">
        <div class="aspect-[3/4] bg-gray-200 dark:bg-gray-700"></div>
        <div class="p-3 space-y-2 bg-white dark:bg-gray-800">
          <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div class="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        </div>
      </div>
    </div>

    <!-- Device Tabs -->
    <div v-if="deviceTabs.length" class="mb-5">
      <DeviceGalleryTabs
        :model-value="selectedDeviceTab"
        :tabs="deviceTabs"
        @update:model-value="handleDeviceTabChange"
      />
    </div>

    <!-- Regular Grid View - Using Device-Specific Cards -->
    <div v-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
      <component
        v-for="wallpaper in filteredWallpapers"
        :key="wallpaper.id"
        :is="activeDeviceCardComponent"
        :wallpaper="wallpaper"
        :category-map="categoryMap"
        :show-admin-actions="false"
        @view="handleWallpaperClick"
        @download="handleDownloadWallpaper"
        @favorite="handleFavoriteToggled"
      />
    </div>

    <!-- Device Preview Mode -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
      <div v-for="wallpaper in filteredWallpapers" :key="wallpaper.id"
        class="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6"
        @click="handleWallpaperClick(wallpaper)">
        <!-- Header with title and badges -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex-1 min-w-0">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {{ wallpaper.title || 'Untitled Wallpaper' }}
            </h3>
            <div class="flex items-center space-x-2 mt-1">
              <span v-if="wallpaper.isWallpaperPack"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                📦 Pack ({{ getDeviceCount(wallpaper) }} devices)
              </span>
              <span v-else
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                📱 Single
              </span>
            </div>
          </div>
          <div class="text-right text-sm text-gray-500 dark:text-gray-400">
            <div class="flex items-center">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                </path>
              </svg>
              {{ formatDownloadCount(wallpaper.downloadCount || 0) }}
            </div>
          </div>
        </div>

        <!-- Device Mockups -->
        <div class="space-y-4">
          <!-- Mobile Preview -->
          <div v-if="getAvailableDevices(wallpaper).includes('mobile_phone')" class="flex items-center space-x-4">
            <div class="flex-shrink-0">
              <div class="relative w-16 h-28 bg-gray-900 rounded-lg p-1 shadow-lg">
                <div class="w-full h-full bg-gray-800 rounded-md overflow-hidden relative">
                  <!-- Notch -->
                  <div class="absolute top-1 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-gray-900 rounded-full">
                  </div>
                  <!-- Wallpaper (secure transformed with Cloudinary sizing) -->
                  <SimpleSecureImage
                    :image-url="getDeviceSpecificImage(wallpaper, 'mobile_phone')"
                    :wallpaper-id="wallpaper.id"
                    :is-preview="true"
                    :alt="wallpaper.title || 'Mobile wallpaper'"
                    :container-class="'w-full h-full'"
                    :image-class="'w-full h-full object-cover'"
                    :target-aspect="64/112"
                    :target-width="64"
                    :target-height="112"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white">📱 Mobile Phone</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">Perfect for smartphones</div>
            </div>
          </div>

          <!-- Tablet Preview -->
          <div v-if="getAvailableDevices(wallpaper).includes('tablet')" class="flex items-center space-x-4">
            <div class="flex-shrink-0">
              <div class="relative w-20 h-28 bg-gray-900 rounded-lg p-1 shadow-lg">
                <div class="w-full h-full bg-gray-800 rounded-md overflow-hidden">
                  <SimpleSecureImage
                    :image-url="getDeviceSpecificImage(wallpaper, 'tablet')"
                    :wallpaper-id="wallpaper.id"
                    :is-preview="true"
                    :alt="wallpaper.title || 'Tablet wallpaper'"
                    :container-class="'w-full h-full'"
                    :image-class="'w-full h-full object-cover'"
                    :target-aspect="80/112"
                    :target-width="80"
                    :target-height="112"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white">📱 Tablet</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">Optimized for tablets</div>
            </div>
          </div>

          <!-- Desktop Preview -->
          <div v-if="getAvailableDevices(wallpaper).includes('desktop')" class="flex items-center space-x-4">
            <div class="flex-shrink-0">
              <div class="relative w-24 h-16 bg-gray-900 rounded-lg p-1 shadow-lg">
                <div class="w-full h-full bg-gray-800 rounded-sm overflow-hidden">
                  <SimpleSecureImage
                    :image-url="getDeviceSpecificImage(wallpaper, 'desktop')"
                    :wallpaper-id="wallpaper.id"
                    :is-preview="true"
                    :alt="wallpaper.title || 'Desktop wallpaper'"
                    :container-class="'w-full h-full'"
                    :image-class="'w-full h-full object-cover'"
                    :target-aspect="96/64"
                    :target-width="96"
                    :target-height="64"
                  />
                </div>
                <!-- Stand -->
                <div class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-2 bg-gray-700 rounded-b-lg">
                </div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white">🖥️ Desktop</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">High resolution for monitors</div>
            </div>
          </div>

          <!-- Ultrawide Preview -->
          <div v-if="getAvailableDevices(wallpaper).includes('ultrawide')" class="flex items-center space-x-4">
            <div class="flex-shrink-0">
              <div class="relative w-28 h-12 bg-gray-900 rounded-lg p-1 shadow-lg">
                <div class="w-full h-full bg-gray-800 rounded-sm overflow-hidden">
                  <SimpleSecureImage
                    :image-url="getDeviceSpecificImage(wallpaper, 'ultrawide')"
                    :wallpaper-id="wallpaper.id"
                    :is-preview="true"
                    :alt="wallpaper.title || 'Ultrawide wallpaper'"
                    :container-class="'w-full h-full'"
                    :image-class="'w-full h-full object-cover'"
                    :target-aspect="112/48"
                    :target-width="112"
                    :target-height="48"
                  />
                </div>
                <!-- Stand -->
                <div class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-10 h-2 bg-gray-700 rounded-b-lg">
                </div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white">🖥️ Ultrawide</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">Perfect for ultrawide monitors</div>
            </div>
          </div>
        </div>

        <!-- Action Button -->
        <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            class="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02]">
            {{ wallpaper.isWallpaperPack ? 'View Pack' : 'Download' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading more indicator -->
    <div v-if="loading && filteredWallpapers.length > 0" class="flex justify-center py-8">
      <div class="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        <span>Loading more wallpapers...</span>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && filteredWallpapers.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No wallpapers found</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or browse different categories.
      </p>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import ImageViewer from './ImageViewer.vue';
import SmartAd from './SmartAd.vue';
import SimpleSecureImage from './ui/SimpleSecureImage.vue';
import DeviceGalleryTabs from './wallpaper-device/DeviceGalleryTabs.vue';
import MobileWallpaperCard from './wallpaper-device/MobileWallpaperCard.vue';
import TabletWallpaperCard from './wallpaper-device/TabletWallpaperCard.vue';
import LaptopWallpaperCard from './wallpaper-device/LaptopWallpaperCard.vue';
import DesktopWallpaperCard from './wallpaper-device/DesktopWallpaperCard.vue';
import UltrawideWallpaperCard from './wallpaper-device/UltrawideWallpaperCard.vue';
import { DEVICE_WALLPAPER_TABS, filterWallpapersForDevice, getDeviceWallpaperTabs } from '../composables/useDeviceWallpaperTabs';

export default {
  name: 'WallpaperGrid',

  components: {
    ImageViewer,
    SmartAd,
    SimpleSecureImage,
    DeviceGalleryTabs,
    MobileWallpaperCard,
    TabletWallpaperCard,
    LaptopWallpaperCard,
    DesktopWallpaperCard,
    UltrawideWallpaperCard
  },

  props: {
    wallpapers: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    viewMode: {
      type: String,
      default: 'grid',
      validator: (value) => ['grid', 'preview'].includes(value)
    },
    categoryMap: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['wallpaper-click', 'load-more', 'image-loaded'],

  data() {
    return {
      loadingImages: {},
      selectedWallpaper: null,
      imagesLoaded: 0,
      totalImages: 0,
      resizeTimeout: null,
      selectedDeviceTab: DEVICE_WALLPAPER_TABS[0]
    };
  },

  computed: {
    deviceTabs() {
      return getDeviceWallpaperTabs(this.wallpapers || []);
    },

    filteredWallpapers() {
      return filterWallpapersForDevice(this.wallpapers || [], this.selectedDeviceTab);
    },

    activeDeviceCardComponent() {
      const componentMap = {
        mobile_phone: 'MobileWallpaperCard',
        tablet: 'TabletWallpaperCard',
        laptop: 'LaptopWallpaperCard',
        desktop: 'DesktopWallpaperCard',
        ultrawide: 'UltrawideWallpaperCard'
      };

      return componentMap[this.selectedDeviceTab] || 'DesktopWallpaperCard';
    }
  },

  methods: {
    handleDeviceTabChange(device) {
      this.selectedDeviceTab = device;
    },

    handleFavoriteToggled(wallpaperId, isFavorited) {
      // You can add any additional logic here when a wallpaper is favorited/unfavorited
      console.log(`Wallpaper ${wallpaperId} ${isFavorited ? 'added to' : 'removed from'} favorites`);
    },

    handleDownloadWallpaper(wallpaper) {
      // Handle wallpaper download
      console.log('Downloading wallpaper:', wallpaper.title);
      // For now, just open the image in a new tab
      window.open(wallpaper.imageUrl || wallpaper.thumbnailUrl, '_blank');
    },

    handleWallpaperClick(wallpaper, event) {
      // If the click is on a link or button inside the card, don't open the image viewer
      if (event && event.target && event.target.closest('a, button')) {
        return;
      }
      this.selectedWallpaper = wallpaper;
      this.$emit('wallpaper-click', wallpaper);
    },

    handleImageLoad(event) {
      this.$emit('image-loaded');
      // Calculate grid row span for masonry effect
      this.calculateGridRowSpan(event.target);
    },

    calculateGridRowSpan(img) {
      if (!img || this.viewMode !== 'grid') return;

      const item = img.closest('.masonry-item');
      if (!item) return;

      // Wait for image to be fully loaded
      if (!img.complete) return;

      // Reset any existing grid-row-end to get natural height
      item.style.gridRowEnd = 'auto';

      // Wait for next tick to ensure layout is updated
      this.$nextTick(() => {
        // Get the natural height of the image
        const imgHeight = img.naturalHeight;
        const imgWidth = img.naturalWidth;

        if (!imgHeight || !imgWidth) return;

        // Calculate the aspect ratio
        const aspectRatio = imgHeight / imgWidth;

        // Get the current width of the item
        const itemWidth = item.getBoundingClientRect().width;

        // Calculate the natural height the image should have
        const naturalImageHeight = itemWidth * aspectRatio;

        // Use the natural image height without extra padding
        const totalItemHeight = naturalImageHeight;

        // Get the row height from CSS and gap
        const container = this.$refs.masonryContainer;
        if (!container) return;

        const computedStyle = getComputedStyle(container);
        const gap = parseInt(computedStyle.gap) || 16;

        // Get grid-auto-rows value (should be 8px or 6px)
        const gridAutoRows = parseInt(computedStyle.gridAutoRows) || 8;

        // Calculate how many rows this item should span
        const rowSpan = Math.ceil(totalItemHeight / (gridAutoRows + gap));

        // Apply the row span (no artificial minimum - let images be their natural size)
        item.style.gridRowEnd = `span ${rowSpan}`;

        console.log(`Image: ${imgWidth}x${imgHeight}, Aspect: ${aspectRatio.toFixed(2)}, Natural height: ${naturalImageHeight}px, Row span: ${rowSpan}`);
      });
    },

    initMasonryLayout() {
      if (this.viewMode !== 'grid') return;

      // Calculate row spans for all items after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          const container = this.$refs.masonryContainer;
          if (!container) return;

          const items = container.querySelectorAll('.masonry-item');
          items.forEach(item => {
            const img = item.querySelector('img');
            if (img && img.complete) {
              this.calculateGridRowSpan(img);
            }
          });
        }, 100);
      });
    },

    handleWindowResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.initMasonryLayout();
      }, 150);
    },

    formatDownloadCount(count) {
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1) + 'M';
      } else if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'K';
      }
      return count.toString();
    },

    handleScroll() {
      if (this.loading || !this.hasMore) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomThreshold = document.body.offsetHeight - 500; // 500px from bottom

      if (scrollPosition >= bottomThreshold) {
        this.$emit('load-more');
      }
    },

    // Device helper functions
    getDeviceCount(wallpaper) {
      return wallpaper.supportedDevices?.length || 0;
    },

    getSingleDevice(wallpaper) {
      // For single wallpapers, determine the primary device type based on compatibility
      if (wallpaper.deviceCompatibility && wallpaper.deviceCompatibility.length > 0) {
        const legacyTypeMap = {
          'mobile': 'mobile_phone',
          'tablet': 'tablet',
          'desktop': 'desktop',
          'tv': 'ultrawide'
        };

        // Return the first compatible device type
        for (const legacyType of wallpaper.deviceCompatibility) {
          const mappedType = legacyTypeMap[legacyType];
          if (mappedType) {
            return mappedType;
          }
        }
      }

      // Default fallback
      return 'desktop';
    },

    getAvailableDevices(wallpaper) {
      if (wallpaper.isWallpaperPack) {
        // For packs, return supported devices
        return wallpaper.supportedDevices || [];
      } else {
        // For single wallpapers, return compatible devices
        if (wallpaper.deviceCompatibility && wallpaper.deviceCompatibility.length > 0) {
          const legacyTypeMap = {
            'mobile': 'mobile_phone',
            'tablet': 'tablet',
            'desktop': 'desktop',
            'tv': 'ultrawide'
          };

          return wallpaper.deviceCompatibility
            .map(legacyType => legacyTypeMap[legacyType])
            .filter(Boolean);
        }

        // Default fallback - assume desktop compatibility
        return ['desktop'];
      }
    },

    getDeviceDisplayName(device) {
      const deviceMap = {
        'mobile_phone': 'Mobile',
        'tablet': 'Tablet',
        'desktop': 'Desktop',
        'ultrawide': 'Ultrawide'
      };
      return deviceMap[device] || device;
    },

    getDeviceIcon(device) {
      const iconMap = {
        'mobile_phone': '📱',
        'tablet': '📱',
        'desktop': '🖥️',
        'ultrawide': '🖥️'
      };
      return h('span', { class: 'text-xs' }, iconMap[device] || '📱');
    },

    getDeviceSpecificImage(wallpaper, deviceType) {
      // For single wallpapers, use the main image
      if (!wallpaper.isWallpaperPack) {
        return wallpaper.thumbnailUrl || wallpaper.imageUrl;
      }

      // For packs, try to get device-specific image
      // First check deviceVariationsList (new format)
      if (wallpaper.deviceVariationsList) {
        const deviceData = wallpaper.deviceVariationsList.find(d => d.device === deviceType);
        if (deviceData && deviceData.variations && deviceData.variations.length > 0) {
          // Get the primary variation or first one
          const primaryVariation = deviceData.variations.find(v => v.isPrimary) || deviceData.variations[0];
          return primaryVariation.thumbnailUrl || primaryVariation.imageUrl;
        }
      }

      // Fallback to deviceVariations (legacy format)
      if (wallpaper.deviceVariations && wallpaper.deviceVariations[deviceType]) {
        return wallpaper.deviceVariations[deviceType].thumbnailUrl || wallpaper.deviceVariations[deviceType].imageUrl;
      }

      // Final fallback to main image
      return wallpaper.thumbnailUrl || wallpaper.imageUrl;
    },

  },

  watch: {
    wallpapers: {
      immediate: true,
      handler(newWallpapers) {
        const tabs = getDeviceWallpaperTabs(newWallpapers || []);
        const availableTab = tabs.find(tab => tab.count > 0)?.device || DEVICE_WALLPAPER_TABS[0];

        if (!tabs.some(tab => tab.device === this.selectedDeviceTab && tab.count > 0)) {
          this.selectedDeviceTab = availableTab;
        }
      }
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleWindowResize);

    // Initialize masonry layout after component mounts
    this.$nextTick(() => {
      this.initMasonryLayout();
    });
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleWindowResize);

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  },

  updated() {
    // Reinitialize masonry when wallpapers change
    this.$nextTick(() => {
      this.initMasonryLayout();
    });
  }
};
</script>

<style scoped>
.wallpaper-grid {
  width: 100%;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding: 1rem 0.75rem;
}

@media (min-width: 640px) {
  .wallpaper-grid {
    padding: 1.5rem 1rem;
  }
}

/* Pinterest-Style Masonry Layout - CSS Grid with Row Spans */
.masonry-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 8px;
  gap: 16px;
  width: 100%;
}

@media (max-width: 639px) {
  .masonry-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    grid-auto-rows: 6px;
  }
}

@media (min-width: 768px) {
  .masonry-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    grid-auto-rows: 8px;
  }
}

@media (min-width: 1024px) {
  .masonry-container {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    grid-auto-rows: 8px;
  }
}

@media (min-width: 1280px) {
  .masonry-container {
    grid-template-columns: repeat(5, 1fr);
    gap: 24px;
    grid-auto-rows: 8px;
  }
}

/* Masonry items - will have dynamic grid-row-end */
.masonry-item {
  display: block;
  width: 100%;
  box-sizing: border-box;
  /* Grid row span will be calculated dynamically by JavaScript */
}

/* Images maintain natural aspect ratio */
.masonry-item img {
  width: 100%;
  height: auto;
  display: block;
}

/* Smooth image loading */
img {
  transition: opacity 0.3s ease-in-out;
}
</style>